import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useEffect, useState } from "react";
import { doc, getDoc, setDoc, deleteDoc, collection, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { UserIcon, ShieldCheckIcon, UserCircleIcon } from "@heroicons/react/24/solid";
import { FaCrown, FaStar } from "react-icons/fa";
import { useAuth } from "../hooks/useAuth"; // Asegúrate de tener este hook

export default function PublicProfileModal({ isOpen, onClose, user }) {
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stars, setStars] = useState([]);
  const [hasStar, setHasStar] = useState(false);
  const [processingStar, setProcessingStar] = useState(false);
  const currentUser = useAuth();

  const getCareerIcon = (career) => {
    if (!career) return "❓";
    const lower = career.toLowerCase();
    if (lower.includes("medicina")) return "👨‍⚕️🏥⚕️";
    if (lower.includes("enfermería")) return "🩺💉🩹";
    if (lower.includes("odontología")) return "👩🏼‍⚕️🦷🩻";
    if (lower.includes("psicología")) return "🧠💭";
    if (lower.includes("veterinaria")) return "🐾🐕‍🦺";
    if (lower.includes("bioanálisis")) return "🩸🔬🧬";
    if (lower.includes("farmacia")) return "🥼💊";
    if (lower.includes("electromecánica")) return "⚙️🔧";
    if (lower.includes("industrial")) return "🏭📈";
    if (lower.includes("civil")) return "🏗️";
    if (lower.includes("software") || lower.includes("ciberseguridad") || lower.includes("hardware"))
      return "👨🏽‍💻🌐📡";
    if (lower.includes("administración")) return "📊💼";
    if (lower.includes("economía")) return "📈💰";
    if (lower.includes("contabilidad")) return "🧾📉";
    if (lower.includes("mercadeo")) return "📣🛍️";
    if (lower.includes("finanzas")) return "💹💳";
    if (lower.includes("educación")) return "📚👩‍🏫";
    if (lower.includes("lengua")) return "🗣️📖";
    if (lower.includes("literatura")) return "📘🖋️";
    if (lower.includes("matemáticas")) return "➗📐";
    if (lower.includes("primaria")) return "👶📒";
    if (lower.includes("secundaria")) return "👦📗";
    if (lower.includes("derecho")) return "⚖️👨‍⚖️";
    if (lower.includes("química")) return "🧪⚗️";
    if (lower.includes("biología")) return "🦠🔬";
    if (lower.includes("arquitectura")) return "🏛️📏";
    return "🎓";
  };

  const roleStyles = {
    admin: {
      bg: "bg-gradient-to-r from-yellow-400 via-yellow-300 to-yellow-500 dark:from-yellow-600 dark:via-yellow-500 dark:to-yellow-600",
      textColor: "text-yellow-900 dark:text-yellow-200",
      badgeBg: "bg-yellow-200 dark:bg-yellow-700",
      badgeText: "text-yellow-900 dark:text-yellow-200",
      buttonBg: "bg-yellow-600 hover:bg-yellow-700 dark:bg-yellow-700 dark:hover:bg-yellow-800",
      icon: <FaCrown className="w-20 h-20 text-yellow-800 dark:text-yellow-300 mx-auto mb-2 drop-shadow-lg" />,
      roleLabel: "Administrador",
    },
    moderador: {
      bg: "bg-gradient-to-r from-purple-400 via-purple-300 to-purple-500 dark:from-purple-700 dark:via-purple-600 dark:to-purple-700",
      textColor: "text-purple-900 dark:text-purple-200",
      badgeBg: "bg-purple-200 dark:bg-purple-700",
      badgeText: "text-purple-900 dark:text-purple-200",
      buttonBg: "bg-purple-600 hover:bg-purple-700 dark:bg-purple-700 dark:hover:bg-purple-800",
      icon: <ShieldCheckIcon className="w-20 h-20 text-purple-800 dark:text-purple-300 mx-auto mb-2 drop-shadow-lg" />,
      roleLabel: "Moderador",
    },
    institucion: {
      bg: "bg-gradient-to-r from-[#1b9a6f] via-[#27b583] to-[#1b9a6f] dark:from-[#157d5b] dark:via-[#1b9a6f] dark:to-[#157d5b]",
      textColor: "text-white",
      badgeBg: "bg-[#1b9a6f]/20 dark:bg-[#1b9a6f]/40",
      badgeText: "text-[#1b9a6f] dark:text-[#27b583]",
      buttonBg: "bg-[#1b9a6f] hover:bg-[#157d5b] dark:bg-[#157d5b] dark:hover:bg-[#116046]",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="w-20 h-20 text-[#1b9a6f] mx-auto mb-2 drop-shadow-lg"
          fill="currentColor"
          viewBox="0 0 24 24"
        >
          <path d="M12 3L1 9l11 6 9-4.91V17h2V9L12 3z" />
          <path d="M5 13.18V17l7 4 7-4v-3.82L12 17l-7-3.82z" />
        </svg>
      ),
      roleLabel: "Institución",
    },
    docente: {
      bg: "bg-gradient-to-r from-amber-100 via-amber-200 to-amber-100 dark:from-yellow-900 dark:via-amber-800 dark:to-yellow-900",
      textColor: "text-amber-900 dark:text-amber-100",
      badgeBg: "bg-amber-200 dark:bg-amber-700",
      badgeText: "text-amber-900 dark:text-amber-100",
      buttonBg: "bg-amber-600 hover:bg-amber-700 dark:bg-amber-700 dark:hover:bg-amber-800",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="w-20 h-20 text-amber-800 dark:text-amber-200 mx-auto mb-2 drop-shadow-lg"
          fill="currentColor"
          viewBox="0 0 24 24"
        >
          <path d="M12 2L1 7l11 5 9-4.09V17h2V7L12 2z" />
          <path d="M11 12.98L3.74 9.52l-.74.34V17h2v-5.46l6 2.72v3.74h2v-3.74l6-2.72V17h2V9.86l-.74-.34L13 12.98v4.02h-2v-4.02z" />
        </svg>
      ),
      roleLabel: "Docente",
    },
    soporte: {
      bg: "bg-gradient-to-r from-teal-400 via-cyan-400 to-sky-400 dark:from-teal-700 dark:via-cyan-700 dark:to-sky-700",
      textColor: "text-white",
      badgeBg: "bg-teal-200 dark:bg-teal-800",
      badgeText: "text-teal-900 dark:text-teal-100",
      buttonBg: "bg-teal-600 hover:bg-teal-700 dark:bg-teal-700 dark:hover:bg-teal-800",
       icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="w-20 h-20 text-white mx-auto mb-2 drop-shadow-lg"
          fill="currentColor"
          viewBox="0 0 24 24"
        >
          <path d="M22.7 19.3l-6.4-6.4a7 7 0 01-8.6-8.6l2.1 2.1 3.5-3.5-2.1-2.1A7 7 0 0119 10.3l6.4 6.4a2 2 0 01-2.7 2.7zM14 12a5 5 0 100-10 5 5 0 000 10z" />
        </svg>
      ),
      roleLabel: "Soporte",
    },
    usuario: {
      bg: "bg-white dark:bg-gray-800",
      textColor: "text-gray-900 dark:text-gray-100",
      badgeBg: "bg-blue-100 dark:bg-blue-900",
      badgeText: "text-blue-800 dark:text-blue-300",
      buttonBg: "bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800",
      roleLabel: "Estudiante",
    },
  };

  const getRoleKey = (role) => {
    if (!role) return "usuario";
    const r = role.toLowerCase();
    if (r === "admin" || r === "administrador") return "admin";
    if (r === "moderador") return "moderador";
    if (r === "institucion" || r === "institución") return "institucion";
    if (r === "docente") return "docente";
    if (r == "soporte") return "soporte";
    return "usuario";
  };

  useEffect(() => {
    const fetchUserData = async () => {
      if (!user || !user.uid) return;
      setLoading(true);
      try {
        const userDocSnap = await getDoc(doc(db, "users", user.uid));
        const userData = userDocSnap.exists() ? userDocSnap.data() : {};
        setProfileData({
          nombre:   userData.nombre || user.nombre || "Nombre no disponible",
          nickname: userData.nickname || "No definido",
          carrera:  userData.carrera || "No especificada",
          role:     userData.role || "Usuario",
          photoURL: userData.photoURL || null,
          bio:      userData.biografia || "",
        });
      } catch (err) {
        console.error("Error obteniendo datos del usuario:", err);
        setProfileData(null);
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      fetchUserData();
    }
  }, [isOpen, user]);

  useEffect(() => {
    if (!user?.uid) return;
    const starsRef = collection(db, "profiles", user.uid, "stars");

    const unsubscribe = onSnapshot(starsRef, (snapshot) => {
      const starIds = snapshot.docs.map((doc) => doc.id);
      setStars(starIds);
      setHasStar(starIds.includes(currentUser?.uid));
    });

    return () => unsubscribe();
  }, [user?.uid, currentUser?.uid]);

  const toggleStar = async () => {
    if (!currentUser?.uid || !user?.uid || currentUser.uid === user.uid) return;
    setProcessingStar(true);
    const starRef = doc(db, "profiles", user.uid, "stars", currentUser.uid);

    try {
      if (hasStar) {
        await deleteDoc(starRef);
      } else {
        await setDoc(starRef, {
          uid: currentUser.uid,
          timestamp: Date.now(),
        });
      }
    } catch (err) {
      console.error("Error actualizando estrella:", err);
    } finally {
      setProcessingStar(false);
    }
  };

  if (!user) return null;

  const roleKey = getRoleKey(profileData?.role);
  const styles = roleStyles[roleKey];

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="fixed inset-0 z-30 overflow-y-auto" onClose={onClose}>
        <div className="min-h-screen px-4 text-center flex items-center justify-center bg-black/40 dark:bg-black/60">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className={`w-full max-w-md p-6 rounded-2xl shadow-xl relative ${styles.bg}`}>
              <Dialog.Title className={`text-xl font-bold text-center mb-4 ${styles.textColor}`}>
                Perfil público
              </Dialog.Title>

              {loading ? (
                <p className="text-center text-gray-500 dark:text-gray-400 mt-4">Cargando...</p>
              ) : profileData ? (
                <div className="mt-2">
                  {/* Icono grande del rol */}
                  {roleKey !== "usuario" && styles.icon}

                  <div className="flex justify-center mb-4">
                    {profileData.photoURL ? (
                      <img
                        src={profileData.photoURL}
                        alt="Foto de perfil"
                        className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-lg dark:border-gray-700"
                      />
                    ) : (
                      <div
                        className={`w-24 h-24 rounded-full flex items-center justify-center border-4 border-white shadow-lg bg-gray-200 dark:bg-gray-700 ${styles.textColor}`}
                      >
                        <UserIcon className="h-12 w-12" />
                      </div>
                    )}
                  </div>

                  <div className={`text-center space-y-1 mb-3 ${styles.textColor}`}>
                    <p className="text-lg font-semibold">{profileData.nombre}</p>
                    <p className="text-sm opacity-80 dark:opacity-70">@{profileData.nickname}</p>

                    {profileData.bio && (
                    <div className="mt-4 px-4">
                      <div className="bg-white dark:bg-gray-700 rounded-xl shadow-md p-4 border border-gray-200 dark:border-gray-600 transition-all">
                        <p className="text-sm text-gray-800 dark:text-gray-100 leading-relaxed text-center">
                          {profileData.bio}
                        </p>
                      </div>
                    </div>
                  )}
                  </div>

                  
                  {/* ⭐ Estrellas / Seguidores */}
                    <div className="mt-4 flex justify-center mb-6 items-center gap-2">
                      <FaStar className={`w-5 h-5 ${hasStar ? "text-yellow-300" : "text-gray-400 dark:text-gray-600"}`} />
                      <span className="font-semibold text-gray-800 dark:text-gray-100">{stars.length}</span>

                      {/* Mostrar botón solo si el usuario NO es el mismo */}
                      {currentUser?.uid !== user.uid && (
                        <button
                          onClick={toggleStar}
                          disabled={processingStar}
                          className={`ml-3 flex items-center gap-2 px-4 py-2 rounded-full ${styles.buttonBg} text-gray-800 dark:text-white transition`}
                        >
                          {hasStar ? "Quitar Estrella" : "Dar Estrella"}
                        </button>
                      )}
                    </div>

                    <div className="flex justify-center gap-3 mb-3">
                      <span
                        className={`inline-block px-3 py-1 text-xs rounded-full font-semibold ${styles.badgeBg} ${styles.badgeText} text-gray-800 dark:text-gray-100`}
                        title="Carrera"
                      >
                        {profileData.carrera}
                      </span>
                      <span
                        className={`inline-block px-3 py-1 text-xs rounded-full font-semibold ${styles.badgeBg} ${styles.badgeText} text-gray-800 dark:text-gray-100`}
                        title="Rol"
                      >
                        {styles.roleLabel}
                      </span>
                    </div>

                    {/* Icono representativo de la carrera grande */}
                    <div className="text-center text-4xl text-gray-800 dark:text-gray-100">
                      {getCareerIcon(profileData.carrera)}
                    </div>
                </div>
              ) : (
                <p className="text-center text-red-500 dark:text-red-400 mt-4">No se pudo cargar el perfil.</p>
              )}

              <div className="mt-6 text-center">
                <button
                  className="px-4 py-2 text-sm font-medium rounded bg-gray-500 hover:bg-gray-600 text-white"
                  onClick={onClose}
                >
                  Cerrar
                </button>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}
